import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { RelativeTimestamp } from "@/components/chat/RelativeTimestamp";
import type { ChatMonitor, MonitorStatus, MonitorStopReason } from "@/lib/chat";
import { formatElapsed } from "@/lib/format-datetime";
import { groupMonitorLogs } from "@/lib/monitor-logs";
import { resolveSelectedMonitorId } from "@/lib/monitor-selection";
import { BADGE_STYLE, DANGER_BADGE_STYLE, NEUTRAL_BADGE_STYLE } from "@/lib/pane-styles";
import { ROW_HOVER_CLASS, ROW_SELECTED_CLASS } from "@/lib/row-styles";
import { useTick } from "@/lib/useTick";
import { useChatStore } from "@/stores/useChatStore";

const STATUS_BADGE: Record<MonitorStatus, { label: string; style: CSSProperties }> = {
	active: {
		label: "監視中",
		style: { background: "var(--accent-subtle)", color: "var(--accent)" },
	},
	exited: { label: "正常終了", style: NEUTRAL_BADGE_STYLE },
	stopped: { label: "監視停止", style: NEUTRAL_BADGE_STYLE },
	failed: { label: "失敗", style: DANGER_BADGE_STYLE },
};

const STOP_REASON_LABELS: Record<MonitorStopReason, string> = {
	agentStopped: "エージェントにより停止",
	chatArchived: "チャットのアーカイブ",
	chatDeleted: "チャットの削除",
	exited: "正常終了",
	failed: "失敗",
	processGone: "プロセス消失",
	maxLifetime: "最大監視時間到達",
	maxEmissions: "最大イベント数到達",
	maxOutputBytes: "出力サイズ上限到達",
};

const ACTIVE_ELAPSED_TICK_MS = 10000;

const TIMESTAMP_STYLE: CSSProperties = {
	marginLeft: "auto",
	flexShrink: 0,
	fontSize: "var(--text-caption)",
	color: "var(--fg-3)",
};

/**
 * 現在のチャットのモニターを一覧と詳細の2ペインで表示する。左の一覧で選んだモニターの
 * 状態・経過時間・終了理由と、その出力ログ (monitorEvent を monitor_id でまとめたもの) を右に出す。
 */
export function MonitorPane() {
	const monitors = useChatStore((s) => s.monitors);
	const messages = useChatStore((s) => s.messages);
	const logsByMonitor = useMemo(() => groupMonitorLogs(messages), [messages]);
	const [selectedId, setSelectedId] = useState<string | null>(null);

	// 取得前 (null) も空一覧と同じ扱いにする。タブは常時表示されるため、空白のペインは出さない。
	const list = monitors ?? [];
	const shownId = resolveSelectedMonitorId(list, selectedId);
	const shown = list.find((monitor) => monitor.id === shownId) ?? null;

	return (
		<div data-testid="monitor-pane" style={{ height: "100%", minHeight: 0 }}>
			{shown === null ? (
				<p
					style={{
						display: "flex",
						height: "100%",
						alignItems: "center",
						justifyContent: "center",
						margin: 0,
						padding: 16,
						fontSize: "var(--text-caption)",
						color: "var(--fg-3)",
					}}
				>
					モニターはまだありません
				</p>
			) : (
				<PanelGroup direction="horizontal">
					<Panel defaultSize={30} minSize={20}>
						<MonitorList monitors={list} shownId={shown.id} onSelect={setSelectedId} />
					</Panel>
					<PanelResizeHandle className="izanagi-resize-handle" />
					<Panel defaultSize={70} minSize={30}>
						<MonitorDetail
							key={shown.id}
							monitor={shown}
							lines={logsByMonitor.get(shown.id) ?? []}
						/>
					</Panel>
				</PanelGroup>
			)}
		</div>
	);
}

function MonitorList({
	monitors,
	shownId,
	onSelect,
}: {
	monitors: ChatMonitor[];
	shownId: string;
	onSelect: (monitorId: string) => void;
}) {
	return (
		<div style={{ height: "100%", overflowY: "auto", padding: "8px 0" }}>
			{monitors.map((monitor) => {
				const selected = monitor.id === shownId;
				return (
					<button
						key={monitor.id}
						type="button"
						data-testid="monitor-list-item"
						data-monitor-id={monitor.id}
						data-selected={selected ? "true" : "false"}
						onClick={() => onSelect(monitor.id)}
						className={`flex w-full items-center text-left cursor-pointer ${
							selected ? ROW_SELECTED_CLASS : ROW_HOVER_CLASS
						}`}
						style={{
							gap: 8,
							padding: "6px 12px",
							border: 0,
							color: "var(--fg-1)",
							fontFamily: "inherit",
						}}
					>
						<StatusBadge
							source={monitor.source}
							status={monitor.status}
							stopReason={monitor.stopReason}
						/>
						<span
							title={monitor.description}
							data-tooltip-if-truncated
							style={{
								minWidth: 0,
								fontSize: "var(--text-caption)",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{monitor.description}
						</span>
						<RelativeTimestamp iso={monitor.createdAt} style={TIMESTAMP_STYLE} />
					</button>
				);
			})}
		</div>
	);
}

function StatusBadge({
	source,
	status,
	stopReason,
}: Pick<ChatMonitor, "source" | "status" | "stopReason">) {
	const reachedLimit =
		stopReason === "maxLifetime" ||
		stopReason === "maxEmissions" ||
		stopReason === "maxOutputBytes";
	const badge =
		source === "trainingJob" && reachedLimit
			? { label: "監視終了", style: NEUTRAL_BADGE_STYLE }
			: STATUS_BADGE[status];
	return (
		<span
			data-testid="monitor-status-badge"
			data-status={status}
			style={{ ...BADGE_STYLE, ...badge.style }}
		>
			{badge.label}
		</span>
	);
}

function MonitorDetail({ monitor, lines }: { monitor: ChatMonitor; lines: string[] }) {
	const logRef = useRef<HTMLPreElement>(null);

	useEffect(() => {
		const el = logRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [lines.length]);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				padding: 12,
			}}
		>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<StatusBadge
					source={monitor.source}
					status={monitor.status}
					stopReason={monitor.stopReason}
				/>
				<span
					title={monitor.description}
					data-tooltip-if-truncated
					style={{
						minWidth: 0,
						fontWeight: 600,
						fontSize: "var(--text-ui)",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{monitor.description}
				</span>
			</div>
			<div
				style={{
					display: "flex",
					gap: 12,
					flexWrap: "wrap",
					marginTop: 6,
					fontSize: "var(--text-caption)",
					color: "var(--fg-2)",
				}}
			>
				<MonitorElapsed monitor={monitor} />
				{monitor.stopReason && <span>{STOP_REASON_LABELS[monitor.stopReason]}</span>}
				{monitor.exitCode !== null && <span>終了コード: {monitor.exitCode}</span>}
			</div>
			{monitor.source === "trainingJob" && (
				<p style={{ margin: "8px 0 0", fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
					学習ジョブは監視を止めても実行が続きます。状態確認やキャンセルは、このチャットで依頼してください。
				</p>
			)}
			{lines.length > 0 ? (
				<pre
					ref={logRef}
					data-testid={`monitor-log-${monitor.id}`}
					style={{
						flex: 1,
						minHeight: 0,
						marginTop: 8,
						marginBottom: 0,
						overflow: "auto",
						fontFamily: "var(--font-mono)",
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-normal)",
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
						background: "var(--surface-2)",
						borderRadius: "var(--radius-3)",
						padding: 8,
						color: "var(--fg-2)",
					}}
				>
					{lines.join("\n")}
				</pre>
			) : (
				<p
					style={{
						marginTop: 8,
						marginBottom: 0,
						fontSize: "var(--text-caption)",
						color: "var(--fg-3)",
					}}
				>
					出力はまだありません
				</p>
			)}
		</div>
	);
}

function MonitorElapsed({ monitor }: { monitor: ChatMonitor }) {
	const startIso = monitor.startedAt ?? monitor.createdAt;
	if (monitor.status === "active") {
		return <ActiveElapsed startIso={startIso} />;
	}
	if (!monitor.terminalAt) return null;
	const elapsedMs = new Date(monitor.terminalAt).getTime() - new Date(startIso).getTime();
	return <span>監視時間: {formatElapsed(elapsedMs)}</span>;
}

function ActiveElapsed({ startIso }: { startIso: string }) {
	const now = useTick(ACTIVE_ELAPSED_TICK_MS);
	return <span>監視時間: {formatElapsed(now - new Date(startIso).getTime())}</span>;
}
